import type { ReactNode } from 'react';
import type { AccountSubject, ConfiguredTaxObligation, ConfiguredTaxObligationId,
  FilingDeadlineUrgency, KnownFilingDeadline, ResolvedIdentityContext, TaxReturnResource } from '../../lib/api';
import type { ReturnCentreObligation, ReturnCapabilityState } from './return-centre';

export interface ObligationRuntimeState {
  taxReturn?: TaxReturnResource;
  interaction?: ReactNode;
}

export interface FilingDeadlineNotice {
  id: string;
  obligationId: ConfiguredTaxObligationId;
  title: string;
  dueDate: string;
  dateLabel: string;
  urgency: FilingDeadlineUrgency;
  urgencyLabel: string;
  priority: number;
  attentionRequired: boolean;
  authorityLabel: string;
  rule: KnownFilingDeadline['rule'];
}

export function filingDeadlineTone(value: Pick<FilingDeadlineNotice, 'urgency'>): 'neutral' | 'info' | 'warning' | 'danger' {
  if (value.urgency === 'DATE_PASSED' || value.urgency === 'DUE_TODAY' || value.urgency === 'URGENT') return 'danger';
  if (value.urgency === 'DUE_SOON' || value.urgency === 'COMING_UP') return 'warning';
  return 'info';
}

export function filingDeadlineAttentionPriority(value: Pick<FilingDeadlineNotice, 'urgency'>): 'normal' | 'high' | 'urgent' {
  if (value.urgency === 'DATE_PASSED' || value.urgency === 'DUE_TODAY' || value.urgency === 'URGENT') return 'urgent';
  if (value.urgency === 'DUE_SOON') return 'high';
  return 'normal';
}

/** Server-derived deadline projections formatted for the current subject. The
 * client does not infer due dates or urgency. */
export function filingDeadlineNoticesForSubject(subject: AccountSubject | undefined): FilingDeadlineNotice[] {
  const notices = subject?.resolved_contexts?.flatMap((context) => {
    const obligation = context.taxObligation;
    if (context.scope.rail !== 'tax_return' || !obligation?.deadline) return [];
    return [{ id: `${context.contextId}:${obligation.id}:${obligation.deadline.dueDate}`,
      obligationId: obligation.id, title: `${obligation.label} · ${periodLabelFromId(context.scope.periodId)}`,
      dueDate: obligation.deadline.dueDate, dateLabel: formatDeadlineDate(obligation.deadline.dueDate),
      urgency: obligation.deadline.urgency, urgencyLabel: obligation.deadline.urgencyLabel,
      priority: obligation.deadline.priority, attentionRequired: obligation.deadline.attentionRequired,
      authorityLabel: obligation.authorityLabel, rule: obligation.deadline.rule }];
  }) ?? [];
  return notices.sort((left, right) => left.priority - right.priority
    || left.dueDate.localeCompare(right.dueDate) || left.title.localeCompare(right.title));
}

/** Client return choices come from active server-resolved package engagements.
 * The legacy fallback is deliberately narrow: one VAT choice for a business or
 * one PIT choice for an individual, never the platform's entire roadmap. */
export function configuredObligationsForSubject(subject: AccountSubject | undefined): ConfiguredTaxObligation[] {
  if (!subject) return [];
  const configured = unique(subject.resolved_contexts?.filter((context) => context.scope.rail === 'tax_return')
    .flatMap((context) => context.taxObligation ? [context.taxObligation] : []) ?? []);
  if (configured.length > 0) return configured;
  return [legacyObligation(subject.tax_subject_kind === 'individual' || subject.kind === 'individual' ? 'PIT' : 'VAT')];
}

export function returnContextForObligation(subject: AccountSubject | undefined,
  obligationId: ConfiguredTaxObligationId): ResolvedIdentityContext | undefined {
  return subject?.resolved_contexts?.find((context) => context.scope.rail === 'tax_return'
    && (context.taxObligation?.id === obligationId || packageMatches(context.scope.packageId, obligationId)));
}

export function toReturnCentreObligation(obligation: ConfiguredTaxObligation,
  runtime: ObligationRuntimeState = {}): ReturnCentreObligation {
  const capabilities = {
    computation: capability(obligation.capabilities.computation),
    preparation: capability(obligation.capabilities.preparation),
    filing: capability(obligation.capabilities.filing),
    payment: capability(obligation.capabilities.payment),
  };
  const unavailableReason = capabilities.computation.state === 'available' ? undefined
    : 'Calculation is not available for this tax type yet. You can continue with the other tax work shown as available.';
  return {
    id: obligation.id, label: obligation.label, description: obligation.description,
    cadence: obligation.cadence, periodLabel: periodLabel(obligation.packageId, runtime.taxReturn),
    authorityLabel: obligation.authorityLabel, applicability: 'applicable', capabilities,
    ...(obligation.deadline ? { deadline: obligation.deadline } : {}),
    ...(runtime.taxReturn ? { taxReturn: runtime.taxReturn } : {}),
    ...(runtime.interaction ? { interaction: runtime.interaction } : {}),
    ...(unavailableReason ? { unavailableReason } : {}),
  };
}

function capability(value: ConfiguredTaxObligation['capabilities']['computation']) {
  const state: ReturnCapabilityState = value.state === 'AVAILABLE' ? 'available'
    : value.state === 'NOT_IMPLEMENTED' ? 'not-implemented' : 'blocked';
  return { state, detail: value.detail };
}

function unique(values: readonly ConfiguredTaxObligation[]): ConfiguredTaxObligation[] {
  const byId = new Map<ConfiguredTaxObligationId, ConfiguredTaxObligation>();
  for (const value of values) if (!byId.has(value.id)) byId.set(value.id, value);
  return [...byId.values()];
}

function packageMatches(packageId: string, obligationId: ConfiguredTaxObligationId): boolean {
  return packageId.startsWith(`AMOYE-${obligationId}-`);
}

function periodLabel(packageId: string, taxReturn?: TaxReturnResource): string {
  if (taxReturn?.periodStart) return humanPeriod(taxReturn.periodStart, packageId);
  return packageId.includes('-ANNUAL-') ? 'Current tax year' : 'Current filing period';
}

function humanPeriod(periodStart: string, packageId: string): string {
  const [year, month] = periodStart.split('-');
  if (packageId.includes('-ANNUAL-') || !month) return `${year} tax year`;
  const date = new Date(`${year}-${month}-01T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? periodStart
    : date.toLocaleDateString('en-NG', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function periodLabelFromId(periodId: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(periodId);
  if (!match) return /^\d{4}$/.test(periodId) ? `${periodId} tax year` : periodId;
  const date = new Date(`${match[1]}-${match[2]}-01T00:00:00Z`);
  return date.toLocaleDateString('en-NG', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function formatDeadlineDate(value: string): string {
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? value
    : date.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function legacyObligation(id: 'VAT' | 'PIT'): ConfiguredTaxObligation {
  if (id === 'PIT') return {
    id, applicability: 'APPLICABLE', taxpayerRole: 'TAXPAYER',
    packageId: 'AMOYE-PIT-ANNUAL-STATE-CAND-001', label: 'Personal income tax',
    description: 'Annual personal-tax preparation from reviewed income, relief and credit facts.', cadence: 'Annual',
    authorityLabel: 'Applicable State or FCT Internal Revenue Service', capabilities: {
      computation: { state: 'AVAILABLE', detail: 'You can calculate and inspect a candidate personal-tax estimate now.' },
      preparation: { state: 'BLOCKED', detail: 'A professionally approved calculation is required before preparation.' },
      filing: { state: 'BLOCKED', detail: 'The applicable tax-authority filing connection is not active.' },
      payment: { state: 'BLOCKED', detail: 'Payment starts only from an approved tax amount.' },
    },
  };
  return {
    id, applicability: 'APPLICABLE', taxpayerRole: 'TAXPAYER', packageId: 'AMOYE-VAT-NRS-MONTHLY-CAND-001',
    label: 'Value added tax', description: 'Monthly VAT records, estimate, review, and filing steps.', cadence: 'Monthly',
    authorityLabel: 'Nigeria Revenue Service', capabilities: {
      computation: { state: 'AVAILABLE', detail: 'You can calculate and review a VAT estimate now.' },
      preparation: { state: 'BLOCKED', detail: 'Waiting for professional approval of the calculation rules and official filing format.' },
      filing: { state: 'BLOCKED', detail: 'The APP/SI filing connection has not been activated.' },
      payment: { state: 'BLOCKED', detail: 'Payment starts only from an approved tax amount.' },
    },
  };
}
